<template>
  <div class="px-8 pt-3 md:px-24">
    <settings-header
      active-tab="members"
      :entity-id="entityId"
    />
    <div class="flex justify-between mt-10">
      <h1
        class="text-xl text-left text-sc-gray-dark"
      >
        {{ $t('message.settings.membersTab.members') }}
      </h1>
      <button
        v-if="isAdmin"
        class="inline-block px-3 py-1 text-sm leading-5 text-center text-white rounded bg-sc-blue hover:text-white"
        @click="$modal.show('add-member-modal')"
      >
        {{ $t('message.settings.membersTab.addMember') }}
      </button>
    </div>
    <div class="mt-5 mb-10">
      <b-table
        :data="members"
        class="border-t border-sc-gray-light"
        :row-class="() => 'group'"
        hoverable
      >
        <b-table-column
          header-class="py-2 font-semibold text-2xs text-primary"
          field="email"
          :label="columnFormatter($t('message.settings.membersTab.table.header.email'))"
          v-slot="props"
          cell-class="border-0"
        >
          <p
            class="text-sm text-primary"
          >
            {{ props.row.email }}
          </p>
        </b-table-column>
        <b-table-column
          header-class="py-2 font-semibold text-2xs text-primary"
          field="name"
          :label="columnFormatter($t('message.settings.membersTab.table.header.firstName'))"
          v-slot="props"
        >
          <p
            class="text-sm text-primary"
          >
            {{ props.row.firstName }}
          </p>
        </b-table-column>
        <b-table-column
          header-class="py-2 font-semibold text-2xs text-primary"
          field="lastName"
          :label="columnFormatter($t('message.settings.membersTab.table.header.lastName'))"
          v-slot="props"
        >
          <p
            class="text-sm text-primary"
          >
            {{ props.row.lastName }}
          </p>
        </b-table-column>
        <b-table-column
          class="flex flex-col"
          header-class="py-2 font-semibold text-2xs text-primary"
          field="role"
          :label="columnFormatter($t('message.settings.membersTab.table.header.role'))"
          v-slot="props"
        >
          <b-dropdown>
            <button
              class="flex items-center w-full px-3 py-1 space-x-3 text-sm rounded shadow-sc-button focus:outline-none"
              slot="trigger"
            >
              <span>{{ userRole(props.row.isAdmin) }}</span>
              <img
                :src="require('@assets/images/chevron-down-outline.svg')"
                alt="flecha para abajo"
              >
            </button>
            <b-dropdown-item
              v-for="(role, key) in roleTypes"
              :key="`${props.row.id}-${role}`"
              class="px-0"
            >
              <button
                @click="updateRole(props.row, key)"
                class="w-full pl-4 text-xs text-left text-primary"
              >
                {{ role }}
              </button>
            </b-dropdown-item>
          </b-dropdown>
        </b-table-column>
        <b-table-column
          header-class="py-2 font-semibold text-2xs text-primary"
          field="defaultFilter"
          :label="columnFormatter($t('message.settings.membersTab.table.header.defaultFilter'))"
          v-slot="props"
        >
          <b-dropdown>
            <button
              class="flex items-center w-full px-3 py-1 space-x-3 text-sm rounded shadow-sc-button focus:outline-none"
              slot="trigger"
            >
              <span>{{ props.row.defaultFilter ? props.row.defaultFilter.name : 'Ninguno' }}</span>
              <img
                :src="require('@assets/images/chevron-down-outline.svg')"
                alt="flecha para abajo"
              >
            </button>
            <b-dropdown-item
              class="px-0"
            >
              <button
                class="w-full pl-4 text-xs text-left text-primary"
                @click="removeDefaultFilterAsignation(props.row)"
              >
                Ninguno
              </button>
            </b-dropdown-item>
            <b-dropdown-item
              v-for="(defaultFilter) in entityDefaultFilters"
              :key="`${props.row.id}-${defaultFilter.id}`"
              class="px-0"
            >
              <button
                @click="addDefaultFilterAsignation(props.row, defaultFilter)"
                class="w-full pl-4 text-xs text-left text-primary"
              >
                {{ defaultFilter.name }}
              </button>
            </b-dropdown-item>
          </b-dropdown>
        </b-table-column>
        <b-table-column
          header-class="py-2 font-semibold text-2xs text-primary"
          field="downloadsEnabled"
          :label="columnFormatter($t('message.settings.membersTab.table.header.downloadsEnabled'))"
          v-slot="props"
        >
          <b-dropdown
            v-if="!props.row.isAdmin"
          >
            <button
              class="flex items-center w-full px-3 py-1 space-x-3 text-sm rounded shadow-sc-button focus:outline-none"
              slot="trigger"
            >
              <span>
                {{
                  props.row.subscription.downloadsEnabled ?
                    $t('message.settings.membersTab.table.downloadsEnabled.true') :
                    $t('message.settings.membersTab.table.downloadsEnabled.false')
                }}
              </span>
              <img
                :src="require('@assets/images/chevron-down-outline.svg')"
                alt="flecha para abajo"
              >
            </button>
            <b-dropdown-item
              v-for="(option) in downloadsEnabledOptions"
              :key="`${props.row.id}-${option}`"
              class="px-0"
            >
              <button
                @click="updateDownloadsEnabled(props.row)"
                class="w-full pl-4 text-xs text-left text-primary"
              >
                {{ option }}
              </button>
            </b-dropdown-item>
          </b-dropdown>
        </b-table-column>
        <b-table-column
          v-if="isAdmin"
          field="edit"
          v-slot="props"
        >
          <button
            class="visible px-2 text-xs leading-6 bg-white rounded md:invisible shadow-sc-button group-hover:visible text-primary"
            slot="trigger"
            @click="toggleModal(props.row)"
          >
            {{ $t('message.settings.membersTab.table.removeButton') }}
          </button>
        </b-table-column>
      </b-table>
      <confirmation-modal
        ref="modal"
        :content="`¿Está seguro que desea eliminar a
                  ${userToRemove.firstName} ${userToRemove.lastName}
                  ${userToRemove.email}
                  de esta supercartola?`"
        flip-buttons-style
        :is-approval-modal="false"
        @confirmation="removeMember"
      />
    </div>
    <modal
      name="add-member-modal"
      height="auto"
      adaptive
    >
      <div class="flex flex-col mx-4 my-5">
        <p
          class="mt-1 ml-3 text-xl text-left text-sc-gray-dark"
        >
          {{ $t('message.settings.membersTab.addMember') }}
        </p>
        <div
          class="flex justify-between mx-3 mt-4"
        >
          <b-field
            :type="!!error ? 'is-danger' : ''"
            class="w-full mr-3 border-none appearance-none focus:outline-none"
            :message="error"
          >
            <b-input
              type="text"
              placeholder="nuevo@platan.us"
              aria-label="mail"
              v-model="newEmail"
              size="is-small"
            />
          </b-field>
          <b-dropdown
            class="flex-shrink-0"
          >
            <button
              class="w-full py-1 text-sm bg-white rounded text-primary shadow-sc-button"
              slot="trigger"
            >
              <p
                class="px-4"
              >
                <span
                  class="text-secondary"
                >
                  {{ $t('message.settings.membersTab.role') }}:
                </span>
                {{ `${roleTypes[newMemberRole]}` }}
              </p>
            </button>
            <b-dropdown-item
              v-for="(role, key) in roleTypes"
              :key="role"
              class="px-0"
            >
              <button
                @click="newMemberRole= key"
                class="w-full pl-4 text-xs text-left text-primary"
              >
                {{ role }}
              </button>
            </b-dropdown-item>
          </b-dropdown>
        </div>
        <div
          class="flex flex-row justify-end mx-3 mt-10"
        >
          <button
            class="px-3 py-1 text-sm leading-5 text-center text-white rounded justify-content-center bg-sc-blue hover:text-white"
            :class="!validateEmail(newEmail) && 'opacity-50 cursor-not-allowed'"
            @click="addMember"
          >
            {{ $t('message.settings.membersTab.addMember') }}
          </button>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import validateEmail from '../../helpers/validateEmail';
import subscriptionsApi from '../api/subscriptions';
import defaultFiltersApi from '../api/default_filters';
import ConfirmationModal from './confirmation-modal.vue';
import SettingsHeader from './settings-header.vue';
import columnFormatter from '../../helpers/columnFormatter';

export default {
  data() {
    return {
      downloadsEnabledOptions: {
        'true': this.$t('message.settings.membersTab.table.downloadsEnabled.true'),
        'false': this.$t('message.settings.membersTab.table.downloadsEnabled.false'),
      },
      newEmail: null,
      error: null,
      loading: false,
      members: this.initialMembers.reverse(),
      newMemberRole: 'member',
      roleTypes: {
        'admin': this.$t('message.settings.membersTab.roles.admin'),
        'member': this.$t('message.settings.membersTab.roles.member'),
      },
      userToRemove: {},
    };
  },
  components: {
    ConfirmationModal,
    SettingsHeader,
  },
  props: {
    initialMembers: {
      type: Array,
      default: null,
    },
    entityId: {
      type: Number,
      required: true,
    },
    isAdmin: {
      type: Boolean,
      default: false,
    },
    entityDefaultFilters: {
      type: Array,
      required: true,
    },
  },
  methods: {
    validateForm() {
      if (!validateEmail(this.newEmail)) {
        return false;
      }

      return true;
    },
    validateEmail,
    async updateRole(user, role) {
      if (this.adminToMember(user, role) && !this.enoughAdminUsers()) {
        this.$buefy.toast.open({
          message: this.$t('message.settings.membersTab.table.enoughAdmins'),
          type: 'is-danger',
          duration: 3000,
        });

        return;
      }
      const { data: { user: { isAdmin } } } = await subscriptionsApi.updateSubscription(
        user.subscription.id,
        { role },
      );
      user.isAdmin = isAdmin;
    },
    adminToMember(user, role) {
      return user.isAdmin && role === 'member';
    },
    enoughAdminUsers() {
      return this.members.filter(member => member.isAdmin).length > 1;
    },
    async addDefaultFilterAsignation(user, entityDefaultFilter) {
      const { data: { user: { defaultFilter } } } = await defaultFiltersApi.addAsignation(
        this.entityId,
        entityDefaultFilter.id,
        { userId: user.id },
      );
      user.defaultFilter = defaultFilter;
    },
    async removeDefaultFilterAsignation(user) {
      await defaultFiltersApi.removeAsignation(
        this.entityId,
        user.defaultFilter.id,
        { userId: user.id },
      );
      user.defaultFilter = null;
    },
    userRole(isAdmin) {
      return isAdmin ? this.roleTypes.admin : this.roleTypes.member;
    },
    toggleModal(user) {
      this.userToRemove = user;
      this.$refs.modal.show();
    },
    async removeMember() {
      await subscriptionsApi.destroySubscription(this.userToRemove.subscription.id);
      this.members.splice(this.members.indexOf(this.userToRemove), 1);
    },
    async addMember() {
      if (this.validateEmail(this.newEmail)) {
        this.loading = true;
        try {
          const { data: { user } } = await subscriptionsApi.addSubscription({
            email: this.newEmail,
            role: this.newMemberRole },
          this.entityId);
          this.members.push(user);
          this.error = null;
          this.newEmail = null;
          this.$modal.hide('add-member-modal');
        } catch (err) {
          this.error = err.message;
        }
        this.loading = false;
      }
    },
    columnFormatter,
    async updateDownloadsEnabled(user) {
      const { data: { user: { subscription: { downloadsEnabled } } } } = await subscriptionsApi.updateSubscription(
        user.subscription.id,
        { downloadsEnabled: !user.subscription.downloadsEnabled },
      );
      user.subscription.downloadsEnabled = downloadsEnabled;
    },
  },
};
</script>
